import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { ClientConfigService } from '@core/services/client-config/client-config.service';
import { WebConfigAvailabilityService } from '@core/services/webconfig-availability/webconfig-availability.service';
import { ClientStore } from '@core/state/client/client.store';
import { LoaderComponent } from '@shared/components/loader/loader.component';
import { ClientFooterComponent } from '../client-footer/client-footer.component';
import { ClientHeaderComponent } from '../client-header/client-header.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { Button } from 'primeng/button';
import { SpeedDialModule } from 'primeng/speeddial';
import { StatusBannerStripComponent } from '@shared/components/status-banner-strip/status-banner-strip.component';
import { BasePageLayoutComponent } from '../../base-page-layout.component';

@Component({
  selector: 'd2d-client-page-layout',
  imports: [
    RouterOutlet,
    ClientHeaderComponent,
    ClientFooterComponent,
    LoaderComponent,
    SpeedDialModule,
    Button,
    StatusBannerStripComponent,
  ],
  templateUrl: './client-page-layout.component.html',
  styleUrl: './client-page-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('pageTransition', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class ClientPageLayoutComponent extends BasePageLayoutComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly webConfigAvailability = inject(WebConfigAvailabilityService);
  private readonly clientStore = inject(ClientStore);

  public clientConfig = this.clientStore.clientConfig;

  isLoaded = false;
  hideFooter = false;
  hideSpeedDial = false;

  constructor() {
    super('ClientPageLayout');

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isLoaded = false;
      }

      if (event instanceof NavigationEnd && this.environmentService.isBrowser()) {
        this.updateHideFooterFromRoute();

        requestAnimationFrame(() => {
          setTimeout(() => {
            this.isLoaded = true;
          }, 1500);
        });
      } else {
        setTimeout(() => {
          this.isLoaded = true;
        }, 500);
      }
    });

    const config = this.clientConfig();
    if (!config) {
      // Caso límite: el resolver del padre `:client` debería garantizar el config antes de
      // que este layout construya. Si no lo hace, usar el propio slug de la ruta (no hay
      // ruta raíz `/404`, y navegar sin slug caería en el wildcard y perdería el cliente).
      const slug = this.activatedRoute.snapshot.parent?.paramMap.get('client');
      this.router.navigate(slug ? [slug, 'page-not-found'] : ['page-not-found']);
      return;
    }

    this.clientConfigService.changeTheme(config);

    // Verificación en segundo plano (no bloqueante): revalida que el cliente
    // siga activo y su webconfig siga siendo público, sin retrasar el render.
    this.webConfigAvailability.verifyInBackground(config.slug);
  }

  ngOnInit() {
    const clientId = this.clientConfig()?.client_id ?? 0;
    this.loadMenuSettings(clientId);
    this.subscribeToSettingsChanges(clientId);
    this.updateHideFooterFromRoute();
  }

  private updateHideFooterFromRoute(): void {
    let route = this.activatedRoute;
    let hideFooterFound = false;
    let hideSpeedDialFound = false;

    while (route) {
      if (!hideFooterFound && route.snapshot.data['hideFooter'] !== undefined) {
        this.hideFooter = route.snapshot.data['hideFooter'];
        hideFooterFound = true;
      }
      if (!hideSpeedDialFound && route.snapshot.data['hideSpeedDial'] !== undefined) {
        this.hideSpeedDial = route.snapshot.data['hideSpeedDial'];
        hideSpeedDialFound = true;
      }

      if (hideFooterFound && hideSpeedDialFound) break;

      if (route.firstChild) {
        route = route.firstChild;
      } else {
        break;
      }
    }

    if (!hideFooterFound) this.hideFooter = false;
    if (!hideSpeedDialFound) this.hideSpeedDial = false;
  }
}
